import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { Bell, Check, X, Clock, Flame, ShoppingBag, Users, Trophy } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { pl } from 'date-fns/locale';

export function NotificationBell() {
  const { token, authHeaders } = useAuth();
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifs = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/notifications`, { headers: authHeaders() });
      setNotifs(res.data.notifications);
      setUnread(res.data.unread_count);
    } catch {}
  }, [token, authHeaders]);

  useEffect(() => {
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifs]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, null, { headers: authHeaders() });
      setUnread(0);
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const getIcon = (type) => {
    switch (type) {
      case 'streak': return <Flame className="w-3.5 h-3.5 text-zorki" />;
      case 'shop': return <ShoppingBag className="w-3.5 h-3.5 text-primary" />;
      case 'group': return <Users className="w-3.5 h-3.5 text-primary" />;
      case 'reward': return <Trophy className="w-3.5 h-3.5 text-zorki" />;
      default: return <Bell className="w-3.5 h-3.5 text-primary" />;
    }
  };

  if (!token) return null;

  return (
    <div className="relative" data-testid="notification-bell">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-1.5 text-muted-foreground hover:text-white transition-colors"
        data-testid="bell-button"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-primary text-primary-foreground text-[10px] font-bold rounded-full flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border/40 shadow-2xl z-50 max-h-96 overflow-hidden" data-testid="notification-dropdown">
            <div className="flex items-center justify-between p-3 border-b border-border/30">
              <span className="font-heading text-sm font-bold text-white uppercase tracking-wider">Powiadomienia</span>
              {unread > 0 && (
                <button onClick={markAllRead} className="text-xs text-primary hover:text-primary/80 flex items-center gap-1" data-testid="mark-all-read">
                  <Check className="w-3 h-3" /> Przeczytaj wszystkie
                </button>
              )}
            </div>
            <div className="overflow-y-auto max-h-72">
              {notifs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">Brak powiadomień</p>
              ) : (
                notifs.slice(0, 15).map(n => (
                  <div
                    key={n.id}
                    className={`flex items-start gap-2.5 px-3 py-2.5 border-b border-border/20 last:border-0 transition-colors ${
                      n.read ? 'opacity-60' : 'bg-primary/5'
                    }`}
                    data-testid={`notif-${n.id}`}
                  >
                    <div className="mt-0.5">{getIcon(n.type)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white">{n.message}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" />
                        {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: pl }) : ''}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
